import { FC, useEffect, useState } from "react"
import { Grid, Autocomplete, TextField, Button, Typography } from "@mui/material"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { Dayjs } from "dayjs"
import { ApiStatus } from "../../../types/api/status"
import CustomTable from "../../../components/common/CustomTable/CustomTable"
import { moneyCL } from "../../../toolbox/helpers/money-helpers"
import SearchIcon from '@mui/icons-material/Search'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import { useBuildingStore } from "../../../hooks/useBuildingStore"
import { Building } from "../../../types/slices/buildingType"
import { useCartolaBuildingStore } from "../../../hooks/useCartolaBuildingStore"

const columns = [
    { type: 'text', field: 'date', align: 'center', label: 'Fecha' },
    { type: 'text', field: 'charge', align: 'center', label: 'Cargo', format: (row) => moneyCL(row.charge) },
    { type: 'text', field: 'payment', align: 'center', label: 'Abono', format: (row) => moneyCL(row.payment) },
    { type: 'text', field: 'description', align: 'center', label: 'Descripción' },
    { type: 'text', field: 'balance', align: 'center', label: 'Saldo', format: (row) => moneyCL(row.balance) },
]

export const CartolaBuilding: FC = () => {
    const { status: statusBuilding, buildings, getBuildings } = useBuildingStore()
    const { status: statusCartola, cartolaBuildings, getCartolaBuilding, generatePDF } = useCartolaBuildingStore()

    const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)
    const [startDate, setStartDate] = useState<Dayjs | null>(null)
    const [endDate, setEndDate] = useState<Dayjs | null>(null)
    const oneYearAgo = dayjs().subtract(1, 'year')
    const today = dayjs()


    useEffect(() => {
        getBuildings()
    }, [])

    const handleSearch = async () => {
        if (!selectedBuilding || !startDate || !endDate) return

        await getCartolaBuilding({
            idbuilding: selectedBuilding.id,
            start_date: startDate.format('YYYY-MM-DD'),
            end_date: endDate.format('YYYY-MM-DD'),
        })
    }

    const handleDownloadPDF = async () => {
        if (!selectedBuilding || !startDate || !endDate) return

        const response = await generatePDF(
            selectedBuilding.id,
            startDate.format('YYYY-MM-DD'),
            endDate.format('YYYY-MM-DD')
        )

        if (response !== false) {
            const blob = new Blob([response], { type: 'application/pdf' })
            const bloblURL = URL.createObjectURL(blob)
            window.open(bloblURL, '_blank')
        }
    }

    return (
        <>
            <Grid
                item
                container
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginBottom: '10px'
                }}
            >
                <Typography variant='h6' component='h1' gutterBottom sx={{ mt: 1, ml: 1 }} className='title-main'>
                    CARTOLA DE EDIFICIOS
                </Typography>
            </Grid>
            <Grid container spacing={2} alignItems="center" mb={2}>
                <Grid item xs={12} md={4}>
                    <Autocomplete
                        options={buildings}
                        getOptionLabel={(option) => option.name}
                        loading={statusBuilding === ApiStatus.FETCHING}
                        onChange={(_, value) => setSelectedBuilding(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Edificio" size="small" fullWidth />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Desde"
                            value={startDate}
                            onChange={(value) => setStartDate(value)}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            minDate={oneYearAgo}
                            maxDate={today}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Hasta"
                            value={endDate}
                            onChange={(value) => setEndDate(value)}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            minDate={oneYearAgo}
                            maxDate={today}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={6} sm={2} md={1}>
                    <Button
                        onClick={handleSearch}
                        disabled={!selectedBuilding || !startDate || !endDate}
                        startIcon={<SearchIcon />}
                        size="small"
                        variant="contained"
                        sx={{
                            fontSize: "0.75rem",
                            fontWeight: "medium",
                            textTransform: "none",
                            color: "#fff",
                            px: 2,
                            py: 1.35,
                            borderRadius: 3,
                            background: "#212D39",
                            '&:hover': { background: '#212D39', color: '#fff' },
                        }}
                    >
                        Buscar
                    </Button>
                </Grid>
                <Grid item xs={6} sm={2} md={1}>
                    <Button
                        onClick={handleDownloadPDF}
                        disabled={!selectedBuilding || !startDate || !endDate}
                        startIcon={<PictureAsPdfIcon />}
                        size="small"
                        variant="contained"
                        sx={{
                            fontSize: "0.75rem",
                            fontWeight: "medium",
                            textTransform: "none",
                            color: "#fff",
                            px: 2,
                            py: 1.35,
                            borderRadius: 3,
                            background: "#212D39",
                            '&:hover': { background: '#212D39', color: '#fff' },
                        }}
                    >
                        PDF
                    </Button>
                </Grid>
            </Grid>

            <Grid item xs={12}>
                <CustomTable
                    columns={columns}
                    loading={statusCartola === ApiStatus.FETCHING}
                    rows={cartolaBuildings || []}
                    onRowClick={() => { }}
                />
            </Grid>
        </>
    )
}